import { useMemo } from 'react';
import type { Node } from '@xyflow/react';
import type { TersaVideoModel } from '@flowmuse/models/video';
import { computeModelCapabilities } from '@/lib/edges/frame-role';
import { type InlineMediaConfig, classifyNodeMediaType, mergeInlineMedia } from '@flowmuse/models/inline-attachments';
import { nodeHasImageContent, nodeHasVideoContent, nodeHasTextContent } from '@/components/nodes/shared/media-content';

export interface MediaProgress {
  current: number;
  max: number;
}

export interface VideoGuidanceData {
  promptSuggestion?: string;
  imageSuggestion?: string;
  videoSuggestion?: string;
  hasAnySuggestion: boolean;
  needsPrompt: boolean;
  needsImage: boolean;
  needsVideo: boolean;
  imageProgress?: MediaProgress;
  videoProgress?: MediaProgress;
  hasIncomingImageNodes: boolean;
  hasIncomingVideoNodes: boolean;
  hasIncomingTextNodes: boolean;
}

interface UseVideoGuidanceParams {
  selectedModel: TersaVideoModel | null;
  data: any;
  incomers: Node[];
  t: (key: string) => string;
  modelId?: string;
}

const EMPTY_GUIDANCE: VideoGuidanceData = {
  hasAnySuggestion: false,
  needsPrompt: false,
  needsImage: false,
  needsVideo: false,
  hasIncomingImageNodes: false,
  hasIncomingVideoNodes: false,
  hasIncomingTextNodes: false,
};

export function useVideoGuidance({
  selectedModel,
  data,
  incomers,
  t,
  modelId,
}: UseVideoGuidanceParams): VideoGuidanceData {
  // 仅依赖实际用到的字段，避免整个 data 引用变化（分辨率/状态/生成进度等）导致无谓重算。
  const inlineAttachments = data?.inlineAttachments as unknown[] | undefined;
  const frameMode = data?.frameMode as string | undefined;
  const instructions = data?.instructions as string | undefined;
  const dataModelId = data?.model as string | undefined;

  return useMemo(() => {
    if (!selectedModel) return EMPTY_GUIDANCE;

    // 按当前 frameMode 取有效配置（顶层 inlineMedia 与该模式 configByMode 合并），
    // 否则像 PixVerse 这类只在 configByMode 里定义 inlineMedia 的模型会读不到、退化到能力推算的兜底分支。
    const modeInline = frameMode
      ? ((selectedModel as any)?.configByMode?.[frameMode]?.inlineMedia as Record<string, any> | undefined)
      : undefined;
    const imCfg: InlineMediaConfig | undefined = mergeInlineMedia(selectedModel.inlineMedia, modeInline);

    const inlineAtts = Array.isArray(inlineAttachments) ? (inlineAttachments as any[]) : [];
    let inlineImageCount = 0;
    let inlineVideoCount = 0;
    for (const a of inlineAtts) {
      if (!a?.url) continue;
      if (a.fileType === 'image') inlineImageCount++;
      else if (a.fileType === 'video') inlineVideoCount++;
    }
    const hasInlineImages = (imCfg?.image?.allow !== false) && inlineImageCount > 0;
    const hasInlineVideos = (imCfg?.video?.allow !== false) && inlineVideoCount > 0;

    // 单次遍历 incomers，同时统计连接计数与内容标志。
    let connectedImageNodesCount = 0;
    let connectedVideoNodesCount = 0;
    let hasContentImage = false;
    let hasContentVideo = false;
    let hasContentText = false;
    let hasConnectedTextNode = false;
    for (const n of incomers) {
      const ct = classifyNodeMediaType(n as any);
      if (ct === 'image') connectedImageNodesCount++;
      else if (ct === 'video') connectedVideoNodesCount++;
      if (!hasContentImage && nodeHasImageContent(n)) hasContentImage = true;
      if (!hasContentVideo && nodeHasVideoContent(n)) hasContentVideo = true;
      if ((n as any)?.type === 'text') {
        hasConnectedTextNode = true;
        if (!hasContentText && nodeHasTextContent(n)) hasContentText = true;
      }
    }

    const hasIncomingImageNodes = hasContentImage || hasInlineImages;
    const hasIncomingVideoNodes = hasContentVideo || hasInlineVideos;
    const hasIncomingTextNodes = hasContentText;

    const precheck = selectedModel.precheck || [];
    const needsPromptAction =
      precheck.includes('prompt') &&
      !Boolean(instructions?.trim()) &&
      !hasContentText &&
      !hasConnectedTextNode;

    const totalImages = connectedImageNodesCount + inlineImageCount;
    const totalVideos = connectedVideoNodesCount + inlineVideoCount;

    let needsImageAction: boolean;
    let needsVideoAction: boolean;
    let imageProgress: MediaProgress | undefined;
    let videoProgress: MediaProgress | undefined;

    if (imCfg) {
      const imgMax = imCfg.image?.maxCount ?? 1;
      const vidMax = imCfg.video?.maxCount ?? 1;
      needsImageAction = imCfg.image?.allow === true && totalImages < imgMax;
      needsVideoAction = imCfg.video?.allow === true && totalVideos < vidMax;
      if (imCfg.image?.allow && imgMax > 1) imageProgress = { current: totalImages, max: imgMax };
      if (imCfg.video?.allow && vidMax > 1) videoProgress = { current: totalVideos, max: vidMax };
    } else {
      const supportsInput = selectedModel.supportsInput || [];
      const precheckHas = (key: string) => precheck.some((p) => p === key || p.split('|').includes(key));
      const { supportsFirstLast, supportsReference } = computeModelCapabilities(modelId || dataModelId);
      const supportsI2V =
        supportsInput.includes('i2v') ||
        supportsInput.includes('first') ||
        supportsInput.includes('first_last') ||
        supportsInput.includes('reference');
      const supportsV2V = supportsInput.includes('v2v') || selectedModel.supportsEdit;
      const isRefMode = frameMode === 'ref' || frameMode === 'reference';
      const maxAllowedImages =
        isRefMode && supportsReference ? 9 : supportsFirstLast ? 2 : supportsReference ? 9 : 1;
      needsImageAction = (precheckHas('image') || supportsI2V) && totalImages < maxAllowedImages;
      needsVideoAction = (precheckHas('video') || supportsV2V) && totalVideos < 1;
      if (maxAllowedImages > 1) imageProgress = { current: totalImages, max: maxAllowedImages };
    }

    const promptSuggestion = needsPromptAction ? t('Add a prompt to describe what you want to create') : undefined;
    const imageSuggestion = needsImageAction ? t('Connect an image node to enable image-to-video generation') : undefined;
    const videoSuggestion = needsVideoAction ? t('Connect a video node to enable video-to-video generation') : undefined;

    return {
      promptSuggestion,
      imageSuggestion,
      videoSuggestion,
      hasAnySuggestion: Boolean(promptSuggestion || imageSuggestion || videoSuggestion),
      needsPrompt: needsPromptAction,
      needsImage: needsImageAction,
      needsVideo: needsVideoAction,
      imageProgress,
      videoProgress,
      hasIncomingImageNodes,
      hasIncomingVideoNodes,
      hasIncomingTextNodes,
    };
  }, [selectedModel, inlineAttachments, frameMode, instructions, dataModelId, incomers, t, modelId]);
}
